import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  Link,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { paymentApi, ConnectStatus } from '../../services/paymentService';
import { useSalonContextStore } from '../../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill } from '../../components/ui';
import { tokens } from '../../theme';

export const SalonPaymentsPage: React.FC = () => {
  const [params] = useSearchParams();
  const { activeSalonId, salons } = useSalonContextStore();
  const salonId = activeSalonId || salons[0]?.id || null;

  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!salonId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setStatus(await paymentApi.connectStatus(salonId));
    } catch {
      setError('Nie udało się pobrać statusu płatności. Spróbuj odświeżyć status za chwilę.');
    } finally {
      setLoading(false);
    }
  }, [salonId]);

  useEffect(() => {
    load();
  }, [load]);

  const onboard = async () => {
    if (!salonId) return;
    setRedirecting(true);
    setError(null);
    try {
      const { url } = await paymentApi.connectOnboard(salonId);
      window.location.href = url;
    } catch (e) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Nie udało się rozpocząć konfiguracji.');
      setRedirecting(false);
    }
  };

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Ustawienia salonu' }]}
      title="Płatności"
      description="Przyjmuj płatności od klientów za wizyty. Pieniądze trafiają bezpośrednio na Twoje konto przez Stripe."
    />
  );

  if (!salonId) {
    return (
      <Box>
        {header}
        <Paper>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Nie wybrano salonu"
            description="Wybierz salon w górnym pasku, aby skonfigurować płatności."
          />
        </Paper>
      </Box>
    );
  }

  const active = status?.chargesEnabled;
  const started = status?.connected && !status?.chargesEnabled;
  const checklist = [
    { ok: status?.detailsSubmitted, label: 'Dane firmy przesłane do Stripe' },
    { ok: status?.chargesEnabled, label: 'Przyjmowanie płatności włączone' },
    { ok: status?.payoutsEnabled, label: 'Wypłaty na konto bankowe włączone' },
  ];

  return (
    <Box>
      {header}

      <Stack gap={2} sx={{ maxWidth: 760 }}>
        {params.get('return') && <Alert severity="info">Zaktualizowaliśmy status Twojego konta Stripe.</Alert>}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <LoadingState variant="block" height={200} />
        ) : (
          <SectionCard
            title="Konto Stripe"
            subtitle="Płatności kartą i BLIK-iem przez Stripe Connect"
            icon={<CreditCardIcon />}
            action={
              <StatusPill
                label={active ? 'Aktywne' : started ? 'Do dokończenia' : 'Niepołączone'}
                tone={active ? 'success' : started ? 'warning' : 'neutral'}
              />
            }
          >
            <Stack component="ul" gap={1.25} sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
              {checklist.map((row) => (
                <Stack component="li" key={row.label} direction="row" alignItems="center" gap={1.25}>
                  {row.ok ? (
                    <CheckCircleRoundedIcon sx={{ fontSize: 20, color: tokens.success }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: tokens.subtle }} />
                  )}
                  <Typography sx={{ fontSize: '0.9375rem', color: row.ok ? tokens.ink : tokens.inkSoft }}>{row.label}</Typography>
                </Stack>
              ))}
            </Stack>

            {active ? (
              <Alert severity="success" sx={{ mt: 2.5 }}>
                Płatności online są aktywne. Klienci mogą płacić za wizyty przy rezerwacji.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mt: 2.5 }}>
                  {started
                    ? 'Konfiguracja rozpoczęta — dokończ dane w Stripe, aby aktywować płatności.'
                    : 'Połącz konto Stripe, aby przyjmować płatności online. Zakładanie konta i weryfikację danych prowadzi Stripe.'}
                </Alert>
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                  <Button variant="contained" startIcon={<CreditCardIcon />} onClick={onboard} disabled={redirecting}>
                    {redirecting ? 'Przekierowanie…' : started ? 'Dokończ konfigurację' : 'Połącz konto Stripe'}
                  </Button>
                  <Button variant="text" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
                    Odśwież status
                  </Button>
                </Stack>
              </>
            )}
          </SectionCard>
        )}

        <Typography variant="body2" sx={{ color: tokens.muted }}>
          Metody płatności dostępne w kasie i kalendarzu (gotówka, karta, BLIK…) ustawisz w{' '}
          <Link component={RouterLink} to="/salon/profile">Profilu biznesu</Link>.
        </Typography>
      </Stack>
    </Box>
  );
};

export default SalonPaymentsPage;
